export const multipliers = [1, 0.75, 0.5, 0.25];

/**
 * Calculates a total score for a practice based on selected priorities and their weights.
 * @param {Object} metadata - The metadata object for a practice.
 * @param {Array} rankingScore - Array of { priority: string, score: number } objects.
 * @returns {number} totalScore
 */

export const calculatePracticeScore = (practice, rankingScore) => {
  if (!practice || !Array.isArray(rankingScore) || rankingScore.length === 0) {
    return 0;
  }

  return rankingScore.reduce((total, { priority }, index) => {
    const multiplier = multipliers[index] ?? 0.25;

    const matchedPriority = practice.priorities?.find(
      (p) => p.name === priority
    );

    const rating = matchedPriority?.rating ?? 0;

    return total + rating * multiplier;
  }, 0);
};

/**
 * Returns a match label and corresponding class name based on the total score and number of selected priorities.
 * @param {number} score - Total score for the practice.
 * @param {number} totalPriorities - Number of selected priorities.
 * @returns {{scoreLabel: string, scoreClassName: string}}
 */

export const getMatchLabel = (score, totalPriorities) => {
  const ranges = {
    0: [],
    1: [
      { min: 1, max: 1, label: "Fair Match", className: "practice-fair-match" },
      { min: 2, max: 2, label: "Good Match", className: "practice-good-match" },
      { min: 3, max: 3, label: "Best Match", className: "practice-best-match" },
    ],
    2: [
      {
        min: 0.75,
        max: 2.25,
        label: "Fair Match",
        className: "practice-fair-match",
      },
      {
        min: 2.26,
        max: 3.75,
        label: "Good Match",
        className: "practice-good-match",
      },
      {
        min: 3.76,
        max: 5.25,
        label: "Best Match",
        className: "practice-best-match",
      },
    ],
    3: [
      {
        min: 0.5,
        max: 2.5,
        label: "Fair Match",
        className: "practice-fair-match",
      },
      {
        min: 2.6,
        max: 5.0,
        label: "Good Match",
        className: "practice-good-match",
      },
      {
        min: 5.1,
        max: 6.75,
        label: "Best Match",
        className: "practice-best-match",
      },
    ],
    4: [
      {
        min: 0.25,
        max: 2.75,
        label: "Fair Match",
        className: "practice-fair-match",
      },
      {
        min: 2.76,
        max: 5.5,
        label: "Good Match",
        className: "practice-good-match",
      },
      {
        min: 5.6,
        max: 10,
        label: "Best Match",
        className: "practice-best-match",
      },
    ],
  };

  const key = totalPriorities >= 4 ? 4 : totalPriorities;
  const bands = ranges[key] || [];

  for (const range of bands) {
    if (score >= range.min && (range.max === undefined || score <= range.max)) {
      return { scoreLabel: range.label, scoreClassName: range.className };
    }
  }

  return { scoreLabel: "", scoreClassName: "" };
};
